import React, { useState, useEffect } from 'react';
import { useControleDemanda } from '../context/ControleDemandaProvider';
import { useAuth } from '../../shared/context/AuthContext';
import ModalTransferir from './ModalTransferir';
import ModalDevolver from './ModalDevolver';
import HistoricoAnalise from './HistoricoAnalise';
import ImagemDemanda from './ImagemDemanda';
import MapaDemandaFixed from './MapaDemandaFixed';

export default function DetalhesAnalise({ analise: analiseInicial, onVoltar }) {
  const { 
    finalizarAnalise,
    buscarHistoricoAnalise,
    buscarAnaliseDetalhada
  } = useControleDemanda();
  
  const { user } = useAuth();
  const [analise, setAnalise] = useState(analiseInicial);
  const [loading, setLoading] = useState(false);
  const [showModalTransferir, setShowModalTransferir] = useState(false);
  const [showModalDevolver, setShowModalDevolver] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);
  const [historico, setHistorico] = useState([]);

  useEffect(() => {
    carregarDadosCompletos();
  }, [analiseInicial.analisedemanda_id || analiseInicial.id]);

  const carregarDadosCompletos = async () => {
    setLoading(true);
    try {
      // Buscar dados completos da análise usando a view
      const analiseId = analiseInicial.analisedemanda_id || analiseInicial.id;
      const dadosCompletos = await buscarAnaliseDetalhada(analiseId);
      setAnalise(dadosCompletos);
      
      // Carregar histórico
      await carregarHistorico(analiseId);
    } catch (error) {
      console.error('Erro ao carregar dados completos:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarHistorico = async (analiseId = null) => {
    const id = analiseId || analise.analisedemanda_id || analise.id;
    const hist = await buscarHistoricoAnalise(id);
    setHistorico(hist);
  };

  const formatarData = (data) => {
    if (!data) return 'N/A';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrigemInfo = () => {
    if (analise.tipo_usuario_origem === 1) {
      return {
        icon: 'fas fa-mobile-alt',
        text: 'Aplicativo',
        class: 'origem-app'
      };
    }
    if (analise.tipo_usuario_origem === 2) {
      return {
        icon: 'fab fa-whatsapp',
        text: 'WhatsApp',
        class: 'origem-whatsapp'
      };
    }
    return {
      icon: 'fas fa-question',
      text: 'Não identificado',
      class: 'origem-unknown'
    };
  };

  const handleFinalizar = async () => {
    if (window.confirm('Tem certeza que deseja finalizar esta análise?')) {
      const analiseId = analise.analisedemanda_id || analise.id;
      await finalizarAnalise(analiseId, user.id);
      onVoltar();
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando detalhes da análise...</p>
      </div>
    );
  }

  const origem = getOrigemInfo();
  
  // Usar IDs dos status conforme STATUS_CORRETOS.md
  const statusId = analise.analisedemanda_status_id;
  
  // Debug: verificar valores
  console.log('🔍 Debug DetalhesAnalise:', {
    status_descricao: analise.analisedemanda_status_descricao,
    status_id: analise.analisedemanda_status_id,
    analise_completa: analise
  });
  
  // Status IDs corretos para análises:
  // 1 = PENDENTE, 2 = DEVOLVIDO, 3 = TRANSFERIDO, 4 = FINALIZADO
  const estaFinalizada = statusId === 4; // FINALIZADO
  
  // Ocultar todos os botões apenas quando estiver finalizada
  const podeTransferir = !estaFinalizada;
  const podeDevolver = !estaFinalizada;
  const podeFinalizar = !estaFinalizada;
  
  console.log('🔍 Debug Condições Análise:', {
    statusId,
    estaFinalizada,
    podeTransferir,
    podeDevolver,
    podeFinalizar
  });

  return (
    <div className="detalhes-analise">
      <div className="detalhes-header">
        <button className="btn-voltar" onClick={onVoltar}>
          <i className="fas fa-arrow-left"></i>
          Voltar
        </button>
        <div className="analise-titulo">
          <h2>Análise - Demanda #{analise.demanda_id}</h2>
          <div className={`origem-badge ${origem.class}`}>
            <i className={origem.icon}></i>
            {origem.text}
          </div>
        </div>
      </div>

      <div className="detalhes-content">
        <div className="detalhes-main">
          <div className="info-section">
            <h3>Informações da Análise</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Status:</label>
                <span className={`status-badge status-${analise.analisedemanda_status_descricao?.toLowerCase().replace(' ', '-') || 'pendente'}`}>
                  {analise.analisedemanda_status_descricao || 'Pendente'}
                </span>
              </div>
              <div className="info-item">
                <label>Funcionário Responsável:</label>
                <span>{analise.analisedemanda_funcionario_envolvido_nome || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Setor:</label>
                <span>{analise.analisedemanda_setor_descricao || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Data de Criação:</label>
                <span>{formatarData(analise.analisedemanda_criado_em)}</span>
              </div>
              <div className="info-item">
                <label>Última Alteração:</label>
                <span>{formatarData(analise.analisedemanda_alterado_em)}</span>
              </div>
            </div>
          </div>

          {analise.analisedemanda_transferido_para_setor_descricao && (
            <div className="info-section">
              <h3>Transferência</h3>
              <div className="transferencia-content">
                <div className="info-item">
                  <label>Transferido para:</label>
                  <span>{analise.analisedemanda_transferido_para_setor_descricao}</span>
                </div>
              </div>
            </div>
          )}

          {analise.analisedemanda_descricao_retorno && (
            <div className="info-section">
              <h3>Descrição do Retorno</h3>
              <div className="retorno-content">
                <p>{analise.analisedemanda_descricao_retorno}</p>
              </div>
            </div>
          )}

          <div className="info-section">
            <h3>Informações da Demanda Original</h3>
            <div className="demanda-original">
              <div className="info-grid">
                <div className="info-item">
                  <label>Categoria:</label>
                  <span>{analise.demanda_categoria_descricao || 'Não informada'}</span>
                </div>
                <div className="info-item">
                  <label>Data da Demanda:</label>
                  <span>{formatarData(analise.demanda_created_at)}</span>
                </div>
                <div className="info-item">
                  <label>Prioridade:</label>
                  <span className={`prioridade-badge prioridade-${analise.demanda_prioridade?.toLowerCase() || 'normal'}`}>
                    {analise.demanda_prioridade || 'Normal'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Solicitante:</label>
                  <span>{analise.demanda_usuario_nome || 'Não identificado'}</span>
                </div>
                <div className="info-item">
                  <label>Origem:</label>
                  <span>
                    {analise.tipo_usuario_origem === 1 ? (
                      <><i className="fas fa-mobile-alt" style={{marginRight: '5px', color: '#27ae60'}}></i>Aplicativo Móvel</>
                    ) : analise.tipo_usuario_origem === 2 ? (
                      <><i className="fab fa-whatsapp" style={{marginRight: '5px', color: '#25d366'}}></i>WhatsApp</>
                    ) : (
                      'Não identificado'
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Seção específica para análises de iluminação pública (categoria 1) */}
          {analise.demanda_categoria_id === 1 && (analise.numero_poste || analise.latitude_poste || analise.longitude_poste) && (
            <div className="info-section">
              <h3>
                <i className="fas fa-lightbulb" style={{ marginRight: '8px', color: '#ff6f00' }}></i>
                Informações do Poste
              </h3>
              <div className="info-grid">
                {analise.numero_poste && (
                  <div className="info-item">
                    <label>Número do Poste:</label>
                    <span className="poste-numero">
                      <i className="fas fa-hashtag" style={{ marginRight: '5px', color: '#ff6f00' }}></i>
                      {analise.numero_poste}
                    </span>
                  </div>
                )}
                {analise.latitude_poste && analise.longitude_poste && (
                  <div className="info-item">
                    <label>Coordenadas do Poste:</label>
                    <span className="coordenadas-poste">
                      <i className="fas fa-map-marker-alt" style={{ marginRight: '5px', color: '#ff6f00' }}></i>
                      {parseFloat(analise.latitude_poste).toFixed(6)}, {parseFloat(analise.longitude_poste).toFixed(6)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Mini mapa do poste */}
              {analise.latitude_poste && analise.longitude_poste && (
                <div className="poste-mapa-container" style={{ marginTop: '15px' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>
                    <i className="fas fa-map" style={{ marginRight: '6px' }}></i>
                    Localização do Poste
                  </h4>
                  <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
                    <MapaDemandaFixed 
                      latitude={parseFloat(analise.latitude_poste)} 
                      longitude={parseFloat(analise.longitude_poste)}
                      showPosteIcon={true}
                      posteNumero={analise.numero_poste}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="info-section">
            <h3>Descrição e Imagem da Demanda</h3>
            <div className="demanda-content">
              <div style={{ marginBottom: '15px' }}>
                <label style={{ 
                  fontWeight: '600', 
                  color: '#5a6c7d', 
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Descrição da Demanda:
                </label>
                <div style={{
                  background: 'white',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <p style={{ margin: 0, lineHeight: '1.6', color: '#2c3e50' }}>
                    {analise.demanda_descricao || 'Sem descrição'}
                  </p>
                </div>
              </div>
              
              {analise.demanda_imagem && (
                <div>
                  <label style={{ 
                    fontWeight: '600', 
                    color: '#5a6c7d', 
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    Imagem da Demanda:
                  </label>
                  <div className="imagem-content">
                    <ImagemDemanda 
                      src={analise.demanda_imagem} 
                      alt="Imagem da demanda"
                      width="300px"
                      height="200px"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="info-section">
            <h3>Localização</h3>
            <div className="localizacao-grid-balanced">
              {/* Informações de endereço */}
              <div className="endereco-info">
                <div className="endereco-item">
                  <strong>Endereço:</strong> {analise.demanda_endereco || 'Não informado'}
                </div>
                <div className="endereco-item">
                  <strong>Ponto de Referência:</strong> {analise.demanda_pontodereferencia || 'Não informado'}
                </div>
                {analise.demanda_coordenada && (
                  <div className="endereco-item">
                    <strong>Coordenadas:</strong> {analise.demanda_coordenada}
                  </div>
                )}
              </div>
              
              {/* Mapa da localização */}
              <div className="mapa-container">
                <MapaDemandaFixed 
                  demanda={{
                    id: analise.demanda_id,
                    coordenada: analise.demanda_coordenada,
                    endereco: analise.demanda_endereco
                  }} 
                  height="100%"
                />
              </div>
            </div>
          </div>



          {(analise.demanda_url_imagem_solucao || analise.demanda_descricao_solucao) && (
            <div className="info-section">
              <h3>Solução Implementada</h3>
              <div className="solucao-content">
                {analise.demanda_descricao_solucao && (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ 
                      fontWeight: '600', 
                      color: '#5a6c7d', 
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      Descrição da Solução:
                    </label>
                    <div style={{
                      background: 'white',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <p style={{ margin: 0, lineHeight: '1.6', color: '#2c3e50' }}>
                        {analise.demanda_descricao_solucao}
                      </p>
                    </div>
                  </div>
                )}
                
                {analise.demanda_url_imagem_solucao && (
                  <div>
                    <label style={{ 
                      fontWeight: '600', 
                      color: '#5a6c7d', 
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      Imagem da Solução:
                    </label>
                    <div className="imagem-content">
                      <ImagemDemanda 
                        src={analise.demanda_url_imagem_solucao} 
                        alt="Imagem da solução"
                        width="300px"
                        height="200px"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="detalhes-sidebar">
          <div className="acoes-section">
            <h3>Ações</h3>
            <div className="acoes-buttons">
              {podeFinalizar && (
                <button
                  className="btn btn-success"
                  onClick={handleFinalizar}
                >
                  <i className="fas fa-check-double"></i>
                  Finalizar
                </button>
              )}
              
              {podeDevolver && (
                <button
                  className="btn btn-warning"
                  onClick={() => setShowModalDevolver(true)}
                >
                  <i className="fas fa-undo"></i>
                  Devolver
                </button>
              )}
              
              {podeTransferir && (
                <button
                  className="btn btn-info"
                  onClick={() => setShowModalTransferir(true)}
                >
                  <i className="fas fa-exchange-alt"></i>
                  Transferir
                </button>
              )}
            </div>
          </div>

          <div className="historico-section">
            <h3>Histórico</h3>
            <button
              className="btn btn-outline"
              onClick={() => setShowHistorico(!showHistorico)}
            >
              <i className="fas fa-history"></i>
              {showHistorico ? 'Ocultar' : 'Ver'} Histórico
            </button>
            
            {showHistorico && (
              <HistoricoAnalise historico={historico} />
            )}
          </div>
        </div>
      </div>

      {showModalTransferir && (
        <ModalTransferir
          analise={analise}
          onClose={() => setShowModalTransferir(false)}
          onSuccess={() => {
            setShowModalTransferir(false);
            onVoltar();
          }}
        />
      )}

      {showModalDevolver && (
        <ModalDevolver
          analise={analise}
          onClose={() => setShowModalDevolver(false)}
          onSuccess={() => {
            setShowModalDevolver(false);
            onVoltar();
          }}
        />
      )}
    </div>
  );
}